import api from './authService'
import { User } from './userService'

// Types
export interface Project {
  id?: number
  _id: string
  name: string
  description?: string
  code: string
  isActive: boolean
  status: string
  startDate?: Date
  endDate?: Date
  workStartTime: string
  workEndTime: string
  maxLateTime: string
  latitude?: number
  longitude?: number
  radius?: number
  objectId: string
  createdAt?: Date
  updatedAt?: Date
  deletedAt?: Date
  // UI display properties
  projectCode?: string
  projectName?: string
  workingTime?: string
  location?: string
}

// New interface for project users
export interface ProjectUser {
  _id: string
  projectId: string
  username: string
  role: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  user: User

  name?: string
  description?: string
  code?: string
}

export interface ProjectFilter {
  value: string
  label: string
}

export interface ProjectFormData {
  code: string
  name: string
  description: string
  status: string
  isActive: boolean
  startDate?: string
  endDate?: string
  workStartTime: string
  workEndTime: string
  maxLateTime: string
  latitude: string
  longitude: string
  radius: string
}

export interface PaginationParams {
  page: number
  perPage: number
}

export interface ProjectsResponse {
  data: Project[]
  meta: {
    total: number
    page: number
    perPage: number
    totalPages: number
  }
}

export interface ProjectEmployeeData {
  projectId: number
  projectCode: string
  projectName: string
  employees: any[] // Using any for now, should be replaced with proper Employee type
}

export interface ProjectUsersResponse {
  data: ProjectUser[]
  meta: {
    total: number
    page: number
    perPage: number
    totalPages: number
  }
}

// Project API service
const projectService = {
  getProjects: async (
    params: PaginationParams = { page: 1, perPage: 10 },
  ): Promise<{
    projects: Project[]
    filters: { status: ProjectFilter[] }
    pagination: any
  }> => {
    try {
      const response = await api.get('/projects', { params })
      const apiData: ProjectsResponse = response.data

      // Transform API data to match UI requirements
      const projects = apiData.data.map((project, index) => ({
        ...project,
        id: index + 1, // For DataGrid
        projectCode: project.code || project.objectId.substring(0, 8),
        projectName: project.name,
        workingTime: `${project.workStartTime} - ${project.workEndTime}`,
        location:
          project.latitude && project.longitude
            ? `${project.latitude}, ${project.longitude}`
            : 'ไม่ระบุ',
      }))

      // Status filters
      const filters = {
        status: [
          { value: '', label: 'สถานะทั้งหมด' },
          { value: 'NOT_STARTED', label: 'ยังไม่เริ่ม' },
          { value: 'IN_PROGRESS', label: 'กำลังดำเนินการ' },
          { value: 'COMPLETED', label: 'เสร็จสิ้น' },
        ],
      }

      return {
        projects,
        filters,
        pagination: apiData.meta,
      }
    } catch (error) {
      console.error('Error fetching projects:', error)
      throw error
    }
  },

  searchProjects: async (
    status: string,
    keyword: string,
    params: PaginationParams = { page: 1, perPage: 10 },
  ): Promise<{ projects: Project[]; pagination: any }> => {
    try {
      // Build query parameters
      const queryParams = {
        ...params,
        ...(status && { status }),
        ...(keyword && { search: keyword }),
      }

      const response = await api.get('/projects', { params: queryParams })
      const apiData: ProjectsResponse = response.data

      // Transform API data to match UI requirements
      const projects = apiData.data.map((project, index) => ({
        ...project,
        id: index + 1, // For DataGrid
        projectCode: project.code || project.objectId.substring(0, 8),
        projectName: project.name,
        workingTime: `${project.workStartTime} - ${project.workEndTime}`,
        location:
          project.latitude && project.longitude
            ? `${project.latitude}, ${project.longitude}`
            : 'ไม่ระบุ',
      }))

      return {
        projects,
        pagination: apiData.meta,
      }
    } catch (error) {
      console.error('Error searching projects:', error)
      throw error
    }
  },

  getProjectById: async (id: string): Promise<Project> => {
    try {
      const response = await api.get(`/projects/${id}`)
      const project = response.data

      // Transform API data to match UI requirements
      return {
        ...project,
        projectCode: project.code || project.objectId.substring(0, 8),
        projectName: project.name,
        workingTime: `${project.workStartTime} - ${project.workEndTime}`,
        location:
          project.latitude && project.longitude
            ? `${project.latitude}, ${project.longitude}`
            : 'ไม่ระบุ',
      }
    } catch (error) {
      console.error(`Error fetching project with id ${id}:`, error)
      throw error
    }
  },

  addProject: async (projectData: ProjectFormData): Promise<boolean> => {
    try {
      // แปลงข้อมูลจาก ProjectFormData เป็นรูปแบบที่ API ต้องการ
      const apiData = {
        name: projectData.name,
        description: projectData.description,
        code: projectData.code,
        isActive: projectData.isActive,
        status: projectData.status,
        startDate: projectData.startDate
          ? new Date(projectData.startDate)
          : undefined,
        endDate: projectData.endDate
          ? new Date(projectData.endDate)
          : undefined,
        workStartTime: projectData.workStartTime,
        workEndTime: projectData.workEndTime,
        maxLateTime: projectData.maxLateTime,
        latitude: projectData.latitude
          ? parseFloat(projectData.latitude)
          : undefined,
        longitude: projectData.longitude
          ? parseFloat(projectData.longitude)
          : undefined,
        radius: projectData.radius ? parseInt(projectData.radius) : 5,
      }

      // เรียก API เพิ่มโครงการ
      const response = await api.post('/projects', apiData)

      return response.status === 201 || response.status === 200
    } catch (error) {
      console.error('Error adding project:', error)
      throw error
    }
  },

  updateEmployeeWorkStatus: async (
    projectId: number,
    employeeId: number,
    status: string,
  ): Promise<boolean> => {
    try {
      // ในสถานการณ์จริงควรเรียก API อัปเดตสถานะการทำงานของพนักงาน
      console.log(
        'Updating employee work status:',
        projectId,
        employeeId,
        status,
      )

      // จำลองการเรียก API
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return true
    } catch (error) {
      console.error('Error updating employee work status:', error)
      throw error
    }
  },

  getProjectEmployees: async (
    projectId: number,
  ): Promise<ProjectEmployeeData> => {
    try {
      // ในสถานการณ์จริงควรเรียก API ที่ถูกต้อง
      const response = await fetch('/mocks/projectEmployees.json')
      const data = await response.json()
      return data.projectEmployees
    } catch (error) {
      console.error('Error fetching project employees:', error)
      throw error
    }
  },

  getAvailableEmployees: async (projectId: number): Promise<any[]> => {
    try {
      // ในสถานการณ์จริงควรเรียก API ที่ถูกต้อง
      const response = await fetch('/mocks/projectEmployees.json')
      const data = await response.json()
      return data.availableEmployees
    } catch (error) {
      console.error('Error fetching available employees:', error)
      throw error
    }
  },

  addEmployeeToProject: async (
    projectId: number,
    employeeId: number,
  ): Promise<boolean> => {
    try {
      // ในสถานการณ์จริงควรเรียก API เพิ่มพนักงานเข้าโครงการ
      console.log('Adding employee to project:', projectId, employeeId)

      // จำลองการเรียก API
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return true
    } catch (error) {
      console.error('Error adding employee to project:', error)
      throw error
    }
  },

  addEmployeesToProject: async (
    projectId: number | string,
    employeeIds: number[],
  ): Promise<boolean> => {
    try {
      // ในสถานการณ์จริงควรเรียก API เพิ่มพนักงานหลายคนเข้าโครงการ
      console.log('Adding employees to project:', projectId, employeeIds)

      // จำลองการเรียก API
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return true
    } catch (error) {
      console.error('Error adding employees to project:', error)
      throw error
    }
  },

  removeEmployeeFromProject: async (
    projectId: number,
    employeeId: number,
  ): Promise<boolean> => {
    try {
      // ในสถานการณ์จริงควรเรียก API ลบพนักงานออกจากโครงการ
      console.log('Removing employee from project:', projectId, employeeId)

      // จำลองการเรียก API
      await new Promise((resolve) => setTimeout(resolve, 1000))

      return true
    } catch (error) {
      console.error('Error removing employee from project:', error)
      throw error
    }
  },

  updateProject: async (
    id: string,
    formData: ProjectFormData,
  ): Promise<Project> => {
    try {
      // แปลงข้อมูลจากฟอร์มเป็นรูปแบบที่ API ต้องการ
      const apiData = {
        code: formData.code,
        name: formData.name,
        description: formData.description,
        status: formData.status,
        isActive: formData.isActive,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        workStartTime: formData.workStartTime,
        workEndTime: formData.workEndTime,
        maxLateTime: formData.maxLateTime,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude
          ? parseFloat(formData.longitude)
          : undefined,
        radius: parseFloat(formData.radius),
      }

      const response = await api.patch(`/projects/${id}`, apiData)

      if (response.status === 200) {
        return response.data
      } else {
        throw new Error('Failed to update project')
      }
    } catch (error) {
      console.error('Error updating project:', error)
      throw error
    }
  },

  getProjectUsers: async (
    projectId: string,
    params: PaginationParams = { page: 1, perPage: 10 },
  ): Promise<{ users: ProjectUser[]; pagination: any }> => {
    try {
      const response = await api.get(`/projects-users/${projectId}/users`, {
        params,
      })
      const apiData: ProjectUsersResponse = response.data

      return {
        users: apiData.data,
        pagination: apiData.meta,
      }
    } catch (error) {
      console.error(
        `Error fetching project users for project ${projectId}:`,
        error,
      )
      throw error
    }
  },

  deleteProjectUser: async (
    projectId: string,
    username: string,
  ): Promise<boolean> => {
    try {
      await api.delete(`/projects-users/${projectId}/${username}`)
      return true
    } catch (error) {
      console.error(
        `Error deleting user ${username} from project ${projectId}:`,
        error,
      )
      throw error
    }
  },
}

export default projectService
